/*
 * The four pregnancy patient checks — clinic HCG, smoker, family history of congenital abnormality,
 * chicken pox — answered Yes / No / Not asked (KIN-2056).
 *
 * None of the four is an MN-CMS field: they have no SCT code, no LOINC code and no OBX anywhere in
 * the implementation checklist, and nothing in the outbound message reads them. They come from our
 * own designer brief, so the shape is ours to choose and is not dictated by the spec.
 *
 * They were single tick boxes, which Darren rejected because a tick cannot say which answer it is:
 * "its unclear if you are ticking to say yes or no." An untouched box also conflated "no" with
 * "nobody asked", which matters most on the family-history check, where the absence of an
 * abnormality is a statement a GP has to make rather than one we can infer from inaction.
 *
 * Stored as a nullable boolean: the columns are BOOLEAN with no NOT NULL and PregnancyDTO holds the
 * object Boolean, so true / false / null carries the three answers with no backend change.
 */

export const PATIENT_CHECK_ANSWERS = {
  yes: "YES",
  no: "NO",
  notAsked: "NOT_ASKED",
};

export const PATIENT_CHECK_OPTIONS = [
  { label: "Yes", value: PATIENT_CHECK_ANSWERS.yes },
  { label: "No", value: PATIENT_CHECK_ANSWERS.no },
  { label: "Not asked", value: PATIENT_CHECK_ANSWERS.notAsked },
];

/**
 * The answer a stored value reads as.
 *
 * A stored false is a real "No" and never "Not asked". Under the tick boxes an unticked box was the
 * only way to answer at all, so every record written before this change holds false wherever the
 * GP did not tick — re-presenting that as "not asked" would quietly discard answers. Only a null,
 * which the tick boxes could never write, means the question was not put.
 */
export const toPatientCheckAnswer = (storedValue) => {
  if (storedValue === true) return PATIENT_CHECK_ANSWERS.yes;
  if (storedValue === false) return PATIENT_CHECK_ANSWERS.no;

  return PATIENT_CHECK_ANSWERS.notAsked;
};

/**
 * The value an answer is stored as.
 *
 * Not asked is null rather than false, so a pregnancy nobody has been asked about does not go to
 * the server asserting four negatives.
 */
export const fromPatientCheckAnswer = (answer) => {
  if (answer === PATIENT_CHECK_ANSWERS.yes) return true;
  if (answer === PATIENT_CHECK_ANSWERS.no) return false;

  return null;
};

/** How a stored value reads in the read-only view. */
export const patientCheckLabel = (storedValue) =>
  PATIENT_CHECK_OPTIONS.find(
    (option) => option.value === toPatientCheckAnswer(storedValue)
  ).label;

/*
 * The family-history check is the only one of the four whose label cannot be read literally, so its
 * wording lives here and is shared by the form and the read view rather than typed out twice.
 *
 * DO NOT "simplify" this to the plain positive "Family history of congenital abnormality". The
 * direction is deliberate, and it runs opposite to how the column name reads: Yes / true is the GP
 * confirming there is NO such family history, which is exactly what ticking the old "No family
 * history of congenital abnormality" box asserted. KIN-2056 reworded the label but deliberately
 * left the mapping alone, so every pregnancy saved under the tick box still holds false wherever
 * the GP did not tick. Flip the label to the positive and all of that legacy false starts reading
 * as "Yes, there is a family history" — inventing a positive finding on old records. The label
 * cannot be corrected by correcting the data either, because a legacy false does not distinguish
 * "no abnormality" from "never asked".
 *
 * Shortened from "Family history of congenital abnormality ruled out", which needed 373px of the
 * 226px column in the four-column grid, wrapped onto two lines and pushed its dropdown below the
 * other three. Only "of congenital abnormality" was dropped; "ruled out" is kept verbatim because
 * it is the phrase that makes the direction unmistakable — a history that has been ruled out is a
 * history that is not there. The tooltip carries the full sentence and the meaning of each answer,
 * because the short label on its own no longer says family history OF WHAT.
 */
export const FAMILY_HISTORY_CHECK_LABEL = "Family history ruled out";

export const FAMILY_HISTORY_CHECK_INFO = [
  "Family history of congenital abnormality.",
  {
    items: [
      "Yes — confirmed there is no family history of congenital abnormality.",
      "No — there is a family history of congenital abnormality.",
      "Not asked — the question has not been put to the patient.",
    ],
  },
];
